import React from 'react';
import { Box, Typography } from '@mui/material';
import { FieldValue } from '@/widgets/order/order-detail-summary/ui/FieldValue';
import { fontSize, typography } from '@/shared/theme';
import { Markdown } from '@/shared/ui/display/Markdown';
import { useSessionTimelineTheme } from '../theme';
import type { EventField } from '../types/sessionTimeline.types';
import { hasTokens } from '@/shared/lib/sessionTokens/parseTokens';
import { isKnownClientId } from '@/shared/lib/sessionTokens/clientIds';
import { TokenizedValue } from './TokenizedValue';

interface EventCardFieldsProps {
  fields?: readonly EventField[];
}

export function EventCardFields({ fields }: EventCardFieldsProps) {
  const { getFieldContainerSx, getFieldRowSx, getFieldLabelSx, tokens } = useSessionTimelineTheme();

  if (!fields || fields.length === 0) {
    return null;
  }

  return (
    <Box sx={getFieldContainerSx()}>
      {fields.map((field, index) => {
        const valueString = typeof field.value === 'string' ? field.value : String(field.value);

        const isMultiline = field.multiline || field.markdown;
        const isMarkdown = field.markdown;
        const isTokenized = hasTokens(valueString);
        const showClientIcon = isKnownClientId(valueString);

        return (
          <Box
            key={index}
            sx={{
              ...getFieldRowSx(),
              alignItems: 'flex-start',
            }}
          >
            {/* 아이콘 영역 - 항상 렌더링하여 정렬 유지 */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'flex-start',
                justifyContent: 'center',
                color: tokens.field.icon.color,
                fontSize: tokens.field.icon.size,
                flexShrink: 0,
                width: '20px',
                visibility: field.icon ? 'visible' : 'hidden',
                marginTop: '2px',
              }}
            >
              {field.icon}
            </Box>
            <Typography
              sx={{
                ...getFieldLabelSx(),
                marginTop: '2px',
              }}
            >
              {field.label}:
            </Typography>
            <Box
              sx={{
                flex: 1,
                minWidth: 0,
                display: 'flex',
                alignItems: 'flex-start',
              }}
            >
              {isTokenized || showClientIcon ? (
                <TokenizedValue value={valueString} />
              ) : isMarkdown ? (
                <Markdown>{valueString}</Markdown>
              ) : isMultiline ? (
                <Box sx={{ width: '100%', whiteSpace: 'pre-line' }}>
                  <Typography
                    sx={{
                      fontSize: fontSize.sm,
                      fontWeight: typography.cell.fontWeight,
                      color: 'text.primary',
                      display: 'inline-block',
                      padding: '0px 4px',
                    }}
                  >
                    {valueString}
                  </Typography>
                </Box>
              ) : (
                <FieldValue copyable={field.copyable}>{valueString}</FieldValue>
              )}
            </Box>
          </Box>
        );
      })}
    </Box>
  );
}
